import {
  Box,
  Grid,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import visuallyHidden from '@mui/utils/visuallyHidden';
import { isEmpty, isNil, map, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { SortOrder } from 'enums/common.enum';
import { NON_ROUTABLE_REQUESTS_PAGE_SIZE } from 'api/nonRoutableRequest';
import {
  listNonRoutableRequestsByPage,
  updateNonRoutableRequestsSort,
} from 'store/thunks/workQueueThunks';

import {
  NON_ROUTABLE_REASON_LABEL,
  NON_ROUTABLE_SOURCE,
  NON_ROUTABLE_SOURCE_LABEL,
  NON_ROUTABLE_TERMINAL_STATUSES,
  RELAY_ERROR_LABEL,
  isNonRoutableSortKey,
  labelOf,
} from '../constants/nonRoutableRequests';
import parkedAgeLabel from '../utils/nonRoutableRequestAge';
import { formatParkedAt } from '../utils/nonRoutableRequestDates';
import NonRoutableRequestStatusChip from './NonRoutableRequestStatusChip';
import NonRoutableRequestsQueueOptions from './NonRoutableRequestsQueueOptions';

// `sortable` is REQUESTED here and granted by NON_ROUTABLE_SORT_KEYS below — a column whose id is
// not a backend sort key cannot become sortable by editing this list alone. Without that gate, one
// wrong flag makes the query fail GraphQL validation, which carries no NON_ROUTABLE_* code, so the
// whole queue renders "this queue did not load". Patient and lines have no sort key on the backend
// and are therefore not sortable.
const headCells = [
  { id: 'referenceId', label: 'Reference Id', sortable: true },
  // POM-6279. WHO parked the row, which is what decides its actions. NOT SORTABLE: `source` is not
  // a member of NonRoutableRequestsSortKey (the gate below would refuse it anyway). Filterable.
  { id: 'source', label: 'Source', sortable: false },
  // NOT SORTABLE, and not labelled "Partner Id", since the cell shows the NAME. The backend sort
  // key `partnerId` orders by nrr.partner_id -- a uuid -- so leaving it sortable gave an operator
  // an ordering that looks random against what the column displays. Central fill reached the same
  // conclusion for the same reason (CentralFillQueueTable: partnerName, sortable false). Making
  // it sortable again means adding a partnerName sort key to the backend enum first.
  { id: 'partnerId', label: 'Partner', sortable: false },
  { id: 'patientId', label: 'Patient', sortable: false },
  // NOT SORTABLE: `drugLabel` is a correlated catalog lookup, not a column, so the backend has no
  // sort key for it. Same reason Partner above is not sortable.
  { id: 'drugLabel', label: 'Drug', sortable: false },
  { id: 'nonRoutableReason', label: 'Reason', sortable: true },
  // NO "Lines" COLUMN. It is constant at 1 for the life of v1 -- the park predicate refuses any
  // order that is not exactly one line item -- so it cost every operator width to show the same
  // digit on every row. `nonRoutableLineCount` IS STILL REQUESTED and must stay requested: the
  // Bring Back / Cancel / Retry dialogs are handed this same row object and render it as "Line
  // items" (NonRoutableRequestIdentity). Dropping it from the selection because no column shows
  // it would blank that field in all three dialogs, silently.
  //
  // THE LABELS BELOW DELIBERATELY DIFFER FROM THEIR SORT KEYS. `attemptCount` and `parkedAt` are
  // backend enum members of NonRoutableRequestsSortKey and cannot be renamed here -- a head cell
  // whose id is not in that enum fails GraphQL VALIDATION, which carries no NON_ROUTABLE_* code,
  // so the whole queue would read "this queue did not load". Only the LABEL is the operator's.
  { id: 'attemptCount', label: 'Retry Count', sortable: true },
  { id: 'parkedAt', label: 'Created At', sortable: true },
  { id: 'lastUpdatedAt', label: 'Last Updated', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
].map((headCell) => ({
  ...headCell,
  sortable: headCell.sortable && isNonRoutableSortKey(headCell.id),
}));

const NonRoutableRequestsQueueTable = () => {
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage, count } = useSelector(
    ({ workQueue }) => workQueue.nonRoutableRequests
  );

  const totalPages = Math.ceil(count / NON_ROUTABLE_REQUESTS_PAGE_SIZE);
  const rows = pages[currentPage];

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      updateNonRoutableRequestsSort({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.DESCENDING
              ? SortOrder.ASCENDING
              : SortOrder.DESCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(listNonRoutableRequestsByPage({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Grid container direction='column'>
      <Grid item>
        <TableContainer>
          <Table size='small' aria-label='Non-routable requests'>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'desc'
                        }
                        data-head-property={headCell.id}
                        onClick={handleSort}
                      >
                        {headCell.label}
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(rows, (row) => {
                const isTerminal = NON_ROUTABLE_TERMINAL_STATUSES.has(row.status);
                // THE AGE IS A TRIAGE SIGNAL, so it is suppressed on a closed request. "8 months
                // ago" under a cancelled row presents urgency about something that needs none,
                // and this queue's whole job is to make the rows that DO need a human obvious.
                // POM-6279: measured on the DATABASE clock (ageSeconds), not the workstation's.
                const age = isTerminal ? null : parkedAgeLabel(row);
                const isRelayFax = row.source === NON_ROUTABLE_SOURCE.RELAY_FAX;

                return (
                  <TableRow key={row.nonRoutableRequestId} hover>
                    <TableCell component='th' scope='row' sx={{ whiteSpace: 'nowrap' }}>
                      {row.referenceId}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {labelOf(NON_ROUTABLE_SOURCE_LABEL, row.source)}
                    </TableCell>
                    {/* NAME WITH THE ID AS FALLBACK, never a blank cell. Both names come from
                        LEFT JOINs, so either can be null, and on this screen an empty cell would
                        be worse than an ugly one: the id is what an operator quotes to support. */}
                    <TableCell>{row.partnerName || row.partnerId}</TableCell>
                    {/* A patient NAME IS served now, from a LEFT JOIN on the patient table --
                        NOT from the parked payload, which is RESTRICTED and stays unprojected.
                        (This comment used to say no name was served; that was true until the
                        product owner asked for names instead of ids.) The link still uses the id.

                        THE LINK PARAM IS AN MPI, AND `patientId` IS ONE — verified, because most
                        of this portal writes `/patients/${mpi}` and only Fill Later uses a field
                        called `patientId`, which makes this look like a copy of the odd one out.
                        It is not: `non_routable_request.patient_id` is written from the stage-3
                        payload's `patient.patientId`, and in smartphos-core that column IS the
                        mpi — `graphql/patient/resolvers.ts` assigns `patientId: args.mpi` and
                        `eScriptPomService.ts` looks patients up by `{ patientId: mpi }`. Fill
                        Later relies on the same identity (`fillLaterActionService.ts` sends
                        `mpi: item.patientId`). Do not "fix" this to `row.mpi`; no such field is
                        served. */}
                    <TableCell>
                      {row.patientId ? (
                        <Link to={`/patients/${row.patientId}`} style={{ color: 'inherit' }}>
                          {row.patientName || row.patientId}
                        </Link>
                      ) : (
                        'N/A'
                      )}
                    </TableCell>
                    {/* WHICH DRUG IS HELD — the thing an operator most needs from this row, and
                        the reason the queue was hard to triage without it.

                        "<drug name> <strength>" comes from the CATALOG (display_name), not from
                        the partner-supplied description on the request, so this screen names a
                        drug the same way every other screen does. On a catalog miss it falls back
                        to the raw product id: this is the only screen a held prescription appears
                        on, so the cell must never be empty. */}
                    <TableCell sx={{ minWidth: 180 }}>
                      {row.drugLabel || row.productId || 'N/A'}
                    </TableCell>
                    {/* On a parked relay fax the reason is always RELAY_FAX_FAILED, so the line that
                        tells rows apart is the fax code under it, and where the fax was going — a
                        pharmacy's business name, not PHI. */}
                    <TableCell sx={{ minWidth: 200 }}>
                      {labelOf(NON_ROUTABLE_REASON_LABEL, row.nonRoutableReason)}
                      {isRelayFax && row.relayErrorCode && (
                        <Typography variant='caption' color='textSecondary' display='block'>
                          {labelOf(RELAY_ERROR_LABEL, row.relayErrorCode)}
                        </Typography>
                      )}
                      {isRelayFax && row.destinationName && (
                        <Typography variant='caption' color='textSecondary' display='block'>
                          {`To ${row.destinationName}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>{row.attemptCount}</TableCell>
                    <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                      {formatParkedAt(row.parkedAt)}
                      {age && (
                        <Typography variant='caption' color='textSecondary' display='block'>
                          {age}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                      {formatParkedAt(row.lastUpdatedAt)}
                    </TableCell>
                    {/* THE CHIP ONLY. A "cancelled by <uuid> on <date>" caption was shown here
                        and was REMOVED on the product owner's instruction: a raw pom user id is
                        not a name, and the column is headed Status, not Resolution. The data is
                        unchanged in the database and still served by the by-id detail read
                        (resolutionType / resolvedAt / resolvedByPomUserId), so a resolution view
                        can show it properly, with the operator's NAME, without a
                        schema change. */}
                    <TableCell>
                      <NonRoutableRequestStatusChip status={row.status} />
                    </TableCell>
                    <TableCell>
                      <NonRoutableRequestsQueueOptions item={row} />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {!isNil(rows) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={headCells.length + 1}
                    align='right'
                    sx={{ borderBottom: 'none' }}
                  >
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(rows) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {/* "Nothing found" means the read SUCCEEDED and returned no rows. A failed read never
          reaches here: the thunk leaves the page unloaded, so the loading state above stays put
          and `handleError` raises the modal. That modal is the only thing separating "nothing is
          parked" from "we could not tell you" — see listNonRoutableRequestsByPage. */}
      {!isNil(rows) && isEmpty(rows) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default NonRoutableRequestsQueueTable;
